import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, Loader2, AlertCircle } from "lucide-react";
import FadeIn from "@/components/FadeIn";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DonationSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", payment_status: null, amount: null });
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", payment_status: null, amount: null });
      return;
    }
    let cancelled = false;
    const MAX = 10;
    const INTERVAL = 2000;

    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await axios.get(`${API}/donations/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", payment_status: "paid", amount: data.amount });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", payment_status: data.payment_status, amount: data.amount });
          return;
        }
        if (attempts.current >= MAX) {
          setState({ status: "timeout", payment_status: data.payment_status, amount: data.amount });
          return;
        }
        setTimeout(poll, INTERVAL);
      } catch (e) {
        if (attempts.current >= MAX) {
          setState({ status: "error", payment_status: null, amount: null });
          return;
        }
        setTimeout(poll, INTERVAL);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div data-testid="donation-success-page" className="min-h-[80vh] flex items-center">
      <div className="container-narrow py-20 text-center">
        {state.status === "polling" && (
          <FadeIn>
            <Loader2 size={36} strokeWidth={1.2} className="mx-auto text-clay animate-spin" />
            <h1 className="mt-8 font-serif text-4xl lg:text-5xl text-moss font-light leading-tight">
              Confirming your gift…
            </h1>
            <p className="mt-6 text-coal/70 text-lg leading-[1.85]">
              One quiet moment — we're just confirming everything with our payment partner.
            </p>
          </FadeIn>
        )}

        {state.status === "paid" && (
          <FadeIn>
            <div className="w-16 h-16 rounded-full bg-moss text-cream flex items-center justify-center mx-auto">
              <Check size={28} strokeWidth={1.5} />
            </div>
            <h1 className="mt-10 font-serif text-5xl lg:text-7xl text-moss font-light leading-[1.05] tracking-tight" data-testid="donation-success-headline">
              Thank you.
            </h1>
            <p className="mt-8 text-coal/75 text-lg lg:text-xl leading-[1.85] max-w-xl mx-auto">
              {state.amount ? (
                <>Your gift of <strong className="text-moss">${Number(state.amount).toFixed(2)}</strong> goes directly to underwriting rest for those who hold the line.</>
              ) : (
                <>Your gift goes directly to underwriting rest for those who hold the line.</>
              )}
            </p>
            <p className="mt-4 text-coal/60 text-sm">A receipt will arrive in your inbox shortly.</p>
            <p className="mt-6 text-coal/60 text-xs leading-[1.85] max-w-md mx-auto italic">
              The Shadow Foundation is a registered 501(c)(3) nonprofit organization.
              Your donation is tax-deductible to the fullest extent allowed by law. Please retain
              this confirmation and the receipt in your inbox for your tax records.
            </p>
            <div className="mt-12 flex flex-wrap gap-4 justify-center">
              <Link to="/" className="btn-primary" data-testid="donation-success-home-btn">Return home</Link>
              <Link to="/shadow-foundation" className="btn-secondary" data-testid="donation-success-shadow-btn">Read the mission</Link>
            </div>
          </FadeIn>
        )}

        {(state.status === "timeout" || state.status === "expired" || state.status === "error") && (
          <FadeIn>
            <AlertCircle size={36} strokeWidth={1.2} className="mx-auto text-clay" />
            <h1 className="mt-8 font-serif text-4xl lg:text-5xl text-moss font-light leading-tight">
              {state.status === "expired" ? "This session expired." : "We couldn't confirm just yet."}
            </h1>
            <p className="mt-6 text-coal/70 text-lg leading-[1.85] max-w-xl mx-auto">
              Please refresh in a moment, or reach out and we'll sort it out personally.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 justify-center">
              <Link to="/donate" className="btn-primary" data-testid="donation-success-retry-btn">Try again</Link>
              <Link to="/visit" className="btn-secondary" data-testid="donation-success-contact-btn">Contact us</Link>
            </div>
          </FadeIn>
        )}
      </div>
    </div>
  );
}
